import { pushEvent } from './dataLayer';
import { getSessionId, getSessionIdSource, type SessionIdSource } from './session';
import {
  getVariantIdForSession,
  getAssignmentSource,
  getVariantBPct,
  type VariantId,
  type AssignmentSource,
} from './variant';
import type { QuizData } from '../types';

// Exposure truth (DEV-1135): what actually RENDERED, set by the store after
// flow/template resolution. The sid-hash variant_id alone is not a valid
// experiment exposure — a broken variant-B config that falls back to the
// legacy renderer still hashes to B. Analysis must key on these fields.
export interface ExposureContext {
  // '' until the store resolves; 'default' when the legacy renderer is active.
  template_id: string;
  // true when a flow/template config was present but invalid at runtime and
  // the renderer fell back to legacy.
  flow_fallback: boolean;
}

let exposure: ExposureContext = { template_id: '', flow_fallback: false };

export function getExposureTemplateId(): string {
  return exposure.template_id;
}

export function setExposureContext(next: ExposureContext): void {
  exposure = { ...next };
}

export function __resetExposureForTests(): void {
  exposure = { template_id: '', flow_fallback: false };
}

export interface EventContext {
  site_slug: string;
  // GA4 reserves `session_id`/`sid` as parameter names (silently dropped at
  // ingest). Use `pblk_sid` on the dataLayer surface; the cookie, internal API
  // payload, and `#l_s` redirect hash keep `session_id`/`pblk_sid` naming as-is.
  pblk_sid: string;
  // 'cookie' when leadclump-flow (or any prior writer) populated pblk_sid before
  // our first read; 'generated' when we minted the SID ourselves. Canary signal
  // for the load-order handshake between leadclump-flow and leadclump-quiz.
  pblk_sid_source: SessionIdSource;
  // Deterministic 50/50 A/B bucket from pblk_sid hash. GTM tag (leadclump-flow#23)
  // forwards this to GA4 as the `variant_id` custom dimension; ClickHouse already
  // has the column. Same sid → same bucket for the session lifetime.
  variant_id: VariantId;
  // 'hash' normally; 'forced' when ?pblk_variant overrode the bucket (exclude
  // from analysis); 'control' when no sid existed (effectively never in prod).
  assignment_source: AssignmentSource;
  // Configured B share the session was bucketed under (DEV-1737), 50 when the
  // quiz has no `variant_b_pct`. Lets the dashboard's split-health gate know
  // what split to expect instead of assuming 50/50.
  variant_b_pct: number;
  // Rendered-exposure truth (DEV-1135) — see ExposureContext above.
  template_id: string;
  flow_fallback: boolean;
}

export interface QuizStartPayload extends EventContext {
  quiz_id: string;
  quiz_name: string;
}
export interface QuizQuestionPayload extends EventContext {
  quiz_id: string;
  question_index: number;
  question_text: string;
  answer_text: string;
}
export interface QuizGateStartPayload extends EventContext {
  quiz_id: string;
  questions_answered: number;
}
export interface QuizGateInputPayload extends EventContext {
  quiz_id: string;
}
export interface QuizSubmitPayload extends EventContext {
  quiz_id: string;
  email_sha256: string;
  redirect_url: string;
}
export interface QuizGateSkipPayload extends EventContext {
  quiz_id: string;
  questions_answered: number;
  redirect_url: string;
}
export interface QuizAnswerSkipPayload extends EventContext {
  quiz_id: string;
  question_index: number;
  question_text: string;
  answer_text: string;
}
export interface FlowErrorPayload extends EventContext {
  quiz_id: string;
  // Truncated like variants_error — GA4 drops >1024-char fields at ingest.
  error_message: string;
}

export interface VariantsErrorPayload extends EventContext {
  quiz_id: string;
  // Truncated to first 200 chars before push. GA4 silently drops fields >1024
  // chars, defeating observability at exactly the moment we need it.
  // The user's bucket is already in EventContext.variant_id — no need to also
  // record the attempted variant since applyVariant only fires this event with
  // the same variantId the bucket resolves to in this same session.
  error_message: string;
}

type CallerInput<T extends EventContext> = Omit<T, keyof EventContext>;

let cachedSiteSlug = '';

function setSiteSlug(quizData: QuizData): void {
  cachedSiteSlug = deriveSiteSlug(quizData);
}

/**
 * Public site-slug primer. Call this AFTER quiz data has loaded but BEFORE any
 * analytics event might fire. Today's only non-dispatch caller is the store's
 * initializeQuizData, which primes the slug before applyVariant runs so that
 * a variants_error event during merge ships with the right site_slug.
 *
 * dispatchQuizStartIfFresh also calls the internal setSiteSlug — that call is
 * harmless and idempotent (deriveSiteSlug is pure).
 */
export function primeSiteSlugForEvents(quizData: QuizData): void {
  setSiteSlug(quizData);
}

function getEventContext(): EventContext {
  if (!cachedSiteSlug && import.meta.env.DEV) {
    console.warn('[analytics] event fired before site_slug was set; payload will ship with empty site_slug');
  }
  const pblkSid = getSessionId();
  return {
    site_slug: cachedSiteSlug,
    pblk_sid: pblkSid,
    pblk_sid_source: getSessionIdSource(),
    variant_id: getVariantIdForSession(pblkSid),
    assignment_source: getAssignmentSource(),
    variant_b_pct: getVariantBPct(),
    template_id: exposure.template_id,
    flow_fallback: exposure.flow_fallback,
  };
}

export function __setSiteSlugForTests(value: string): void {
  cachedSiteSlug = value;
}

export function deriveSiteSlug(quizData: QuizData): string {
  try {
    const host = new URL(quizData.url).hostname.replace(/^www\./, '');
    if (host === 'localhost' || host === '') return quizData.site_id || '';
    return host;
  } catch {
    return quizData.site_id || '';
  }
}

export function trackQuizStart(p: CallerInput<QuizStartPayload>): void {
  pushEvent('quiz_start', { ...p, ...getEventContext() });
}
export function trackQuizQuestion(p: CallerInput<QuizQuestionPayload>): void {
  pushEvent('quiz_question', { ...p, ...getEventContext() });
}
export function trackQuizGateStart(p: CallerInput<QuizGateStartPayload>): void {
  pushEvent('quiz_gate_start', { ...p, ...getEventContext() });
}
export function trackQuizGateInput(p: CallerInput<QuizGateInputPayload>): void {
  pushEvent('quiz_gate_input', { ...p, ...getEventContext() });
}
export function trackQuizSubmit(p: CallerInput<QuizSubmitPayload>): void {
  pushEvent('quiz_submit', { ...p, ...getEventContext() });
}
export function trackQuizGateSkip(p: CallerInput<QuizGateSkipPayload>): void {
  pushEvent('quiz_gate_skip', { ...p, ...getEventContext() });
}
export function trackQuizAnswerSkip(p: CallerInput<QuizAnswerSkipPayload>): void {
  pushEvent('quiz_answer_skip', { ...p, ...getEventContext() });
}

const VARIANTS_ERROR_MESSAGE_MAX_CHARS = 200;

export function trackFlowError(p: CallerInput<FlowErrorPayload>): void {
  const truncated = String(p.error_message ?? '').slice(0, VARIANTS_ERROR_MESSAGE_MAX_CHARS);
  pushEvent('flow_error', {
    ...p,
    ...getEventContext(),
    error_message: truncated,
  });
}

export function trackVariantsError(p: CallerInput<VariantsErrorPayload>): void {
  const truncated = String(p.error_message ?? '').slice(0, VARIANTS_ERROR_MESSAGE_MAX_CHARS);
  // Spread order matches the 7 other track helpers above: p first, context last.
  // error_message goes AFTER both spreads so truncation always wins, even if a
  // future EventContext field collision adds an error_message of its own.
  pushEvent('variants_error', {
    ...p,
    ...getEventContext(),
    error_message: truncated,
  });
}

export function dispatchQuizStartIfFresh(quizData: QuizData): void {
  setSiteSlug(quizData);
  const quizId = quizData.quiz_id;
  if (!quizId) return;
  const dedupKey = `mtdm_quiz_start_${quizId}`;
  let alreadyFired = false;
  try {
    alreadyFired = sessionStorage.getItem(dedupKey) === '1';
  } catch {
    // sessionStorage blocked (e.g. iOS private mode); fail open
  }
  if (alreadyFired) return;
  trackQuizStart({
    quiz_id: quizId,
    quiz_name: quizData.name || '',
  });
  try {
    sessionStorage.setItem(dedupKey, '1');
  } catch {
    // swallow
  }
}
